import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { 
  Loader2, ZoomIn, ZoomOut, ChevronLeft, ChevronRight,
  PenTool, Type, Calendar, User, Mail, Building, FileText,
  Fingerprint, CheckSquare, ThumbsUp, DollarSign
} from "lucide-react";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Field type icons and colors
const FIELD_CONFIG = {
  signature: { icon: PenTool, color: "bg-blue-500", label: "Signature" },
  initials: { icon: Fingerprint, color: "bg-blue-400", label: "Initials" },
  first_name: { icon: User, color: "bg-purple-500", label: "First Name" },
  last_name: { icon: User, color: "bg-purple-400", label: "Last Name" },
  date_signed: { icon: Calendar, color: "bg-green-500", label: "Date Signed" },
  full_name: { icon: User, color: "bg-purple-500", label: "Full Name" },
  email: { icon: Mail, color: "bg-pink-500", label: "Email" },
  title: { icon: Type, color: "bg-orange-500", label: "Title" },
  company: { icon: Building, color: "bg-teal-500", label: "Company" },
  custom_text: { icon: Type, color: "bg-gray-500", label: "Text" },
  checkbox: { icon: CheckSquare, color: "bg-indigo-500", label: "Checkbox" },
  approve: { icon: ThumbsUp, color: "bg-emerald-500", label: "Approve" },
  advisor_name: { icon: User, color: "bg-green-500", label: "Advisor Name" },
  advisor_company: { icon: Building, color: "bg-green-400", label: "Advisor Company" },
  fee_amount: { icon: DollarSign, color: "bg-amber-500", label: "Fee Amount" },
};

// Signer type colors (border and background)
const SIGNER_COLORS = {
  client: "border-blue-500 bg-blue-100/90",
  additional: "border-cyan-500 bg-cyan-100/90",
  advisor: "border-green-500 bg-green-100/90",
};

const DocumentPreviewModal = ({ open, onClose, proposalId, token }) => {
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [pdfData, setPdfData] = useState(null);
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 }); // Default US Letter
  const pageRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && proposalId) {
      loadPreview();
    }
    return () => {
      setPdfData(null);
      setPreviewData(null);
      setCurrentPage(1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposalId]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      // Fetch preview data (signers, field mappings)
      const response = await axios.get(
        `${API}/docusign/proposals/${proposalId}/envelope-preview`,
        { headers, withCredentials: true }
      );
      setPreviewData(response.data);
      
      // Fetch PDF as blob and convert to base64
      if (response.data.agreement?.doc_id) {
        const pdfResponse = await axios.get(
          `${API}/docusign/documents/${response.data.agreement.doc_id}/preview`,
          { 
            headers, 
            withCredentials: true,
            responseType: 'blob'
          }
        );
        
        // Convert blob to base64 data URL
        const reader = new FileReader();
        reader.onloadend = () => {
          setPdfData(reader.result);
        };
        reader.readAsDataURL(pdfResponse.data);
      }
    } catch (error) {
      console.error("Failed to load preview:", error);
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    // Get actual rendered page dimensions
    setPageSize({
      width: page.width,
      height: page.height
    });
  };

  // Get fields for current page
  const getFieldsForPage = (page) => {
    if (!previewData?.field_mappings) return [];
    return previewData.field_mappings.filter(f => (f.page || 1) === page);
  };

  // Render field overlay - coordinates are stored as PERCENTAGES (0-100)
  const renderFieldOverlay = (field, index) => {
    const config = FIELD_CONFIG[field.type] || FIELD_CONFIG.custom_text;
    const Icon = config.icon;
    const signerColor = SIGNER_COLORS[field.signer_type] || "border-gray-400 bg-gray-100/90";
    
    // Field positions are stored as percentages of page dimensions
    // Convert percentage to actual pixels based on rendered page size
    const scaledWidth = pageSize.width * scale;
    const scaledHeight = pageSize.height * scale;
    
    const x = ((field.x || 0) / 100) * scaledWidth;
    const y = ((field.y || 0) / 100) * scaledHeight;
    const width = Math.max(((field.width || 10) / 100) * scaledWidth, 40);
    const height = Math.max(((field.height || 3) / 100) * scaledHeight, 16);

    const signerName = field.signer_name || 'Unassigned';
    const shortName = signerName.split(' ')[0];

    return (
      <div
        key={field.field_id || index}
        className={`absolute border-2 rounded shadow-sm ${signerColor} transition-all hover:shadow-lg hover:z-50 cursor-pointer group`}
        style={{
          left: `${x}px`,
          top: `${y}px`,
          width: `${width}px`,
          height: `${height}px`,
        }}
        title={`${config.label} - ${signerName}`}
      >
        {/* Tooltip on hover */}
        <div className="absolute -top-7 left-0 hidden group-hover:flex items-center gap-1 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50 shadow-lg">
          <Icon className="w-3 h-3" />
          <span>{config.label}</span>
          <span className="opacity-75">• {signerName}</span>
        </div>
        
        {/* Icon and label inside field */}
        <div className="w-full h-full flex items-center justify-center gap-1 text-xs font-medium overflow-hidden px-1">
          <Icon className="w-3 h-3 flex-shrink-0" />
          <span className="truncate opacity-80">{shortName}</span>
        </div>
      </div>
    );
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b bg-white">
          <DialogTitle className="flex items-center justify-between">
            <span>Preview</span>
            <div className="flex items-center gap-2 mr-8">
              {/* Page navigation */}
              {numPages && numPages > 1 && (
                <div className="flex items-center gap-1 mr-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage} of {numPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                    disabled={currentPage >= numPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
              {/* Zoom controls */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
                disabled={scale <= 0.5}
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale(s => Math.min(2, s + 0.25))}
                disabled={scale >= 2}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="w-8 h-8 animate-spin text-forest" />
          </div>
        ) : (
          <div className="flex h-[75vh]">
            {/* Sidebar - Signers & Fields Legend */}
            <div className="w-64 border-r bg-sand/30 p-4 overflow-y-auto flex-shrink-0">
              <h3 className="font-semibold mb-3">Signers</h3>
              <div className="space-y-2 mb-6">
                {previewData?.signers?.map((signer, idx) => (
                  <div 
                    key={signer.signer_id}
                    className={`p-2 rounded border-l-4 ${SIGNER_COLORS[signer.type]}`}
                  >
                    <div className="flex items-center gap-2">
                      {previewData.signing_mode === "in_order" && (
                        <span className="w-5 h-5 rounded-full bg-forest text-white text-xs flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                      )}
                      <span className="font-medium text-sm">{signer.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {signer.type === "advisor" ? "Advisor (countersigns)" : signer.role_title || "Client"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {signer.email}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Tip:</strong> Hover over the colored boxes on the document to see field details.
                </p>
              </div>
            </div>

            {/* Main content - PDF with overlays */}
            <div className="flex-1 overflow-auto bg-gray-200 p-4">
              <div className="inline-block">
                {pdfData ? (
                  <Document
                    file={pdfData}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="w-[612px] h-[792px] bg-white flex items-center justify-center shadow-lg">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    }
                    error={
                      <div className="w-[612px] h-[792px] bg-white flex items-center justify-center shadow-lg">
                        <p className="text-red-500">Failed to load PDF</p>
                      </div>
                    }
                  >
                    <div className="relative shadow-lg" ref={pageRef}>
                      <Page 
                        pageNumber={currentPage} 
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onLoadSuccess={onPageLoadSuccess}
                      />
                      {/* Field overlays for current page */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="relative w-full h-full pointer-events-auto">
                          {getFieldsForPage(currentPage).map((field, idx) => 
                            renderFieldOverlay(field, idx)
                          )}
                        </div>
                      </div>
                    </div>
                  </Document>
                ) : (
                  <div className="w-[612px] h-[792px] bg-white shadow-lg flex items-center justify-center">
                    <p className="text-muted-foreground">No document loaded</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer with signing flow summary */}
        {!loading && previewData && (
          <div className="border-t p-4 bg-sand/30">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <strong>Signing Flow:</strong>{" "}
                {previewData.signing_mode === "all_at_once" ? (
                  <span>
                    All clients receive simultaneously → Advisor countersigns after all clients sign
                  </span>
                ) : (
                  <span>
                    {previewData.signers?.map((s, i) => (
                      <span key={s.signer_id}>
                        {i > 0 && " → "}
                        {s.name}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                {previewData.field_mappings?.length || 0} fields mapped
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewModal;
